import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { isPlatform } from "@ionic/react";

export interface AppInfo {
  version: string;
  platform: "ios" | "android" | "web";
}

export interface MinVersions {
  minIosVersion: string;
  minAndroidVersion: string;
  // minWebVersion: string;
}

// function to get the app info
export const getAppInfo = async (): Promise<AppInfo> => {
  // if the app is running on a native platform, get the app info
  if (Capacitor.isNativePlatform()) {
    const info = await App.getInfo();
    const platform = Capacitor.getPlatform();
    console.log("app info getAppInfo", info.version);
    console.log("platform getAppInfo", platform);
    // return the app info for the native platform
    return {
      version: info.version, // e.g., "1.0.133"
      platform: platform as "ios" | "android",
    };
  }

  //For testing purposes, we can mock the web version
  const mockWebVersion = "1.0.130";

  return {
    version: "web",
    platform: "web",
  };
};

//--------------------------------------------------

/**
 * Compares the current app version with the minimum required version
 * @param platform - The platform the app is running on
 * @param currentVersion - The current version installed on the device
 * @param minVersions - The minimum versions required from the backend
 * @returns true if an update is required, false otherwise
 */
export const isUpdateRequired = (platform: "ios" | "android" | "web", currentVersion: string, minVersions: MinVersions): boolean => {
  // Web platform doesn't need updates
  if (platform === "web") {
    return false;
  }

  // Get the minimum version for the current platform
  const minVersion = platform === "ios" ? minVersions.minIosVersion : minVersions.minAndroidVersion;
  // console.log("isUpdateRequired:", platform, currentVersion, minVersion);
  // Split version strings into arrays of numbers for proper comparison
  // e.g., "1.0.133" becomes [1, 0, 133]
  const currentParts = currentVersion.split(".").map(Number);
  const minParts = minVersion.split(".").map(Number);

  // Find the maximum length to handle versions with different number of parts
  const len = Math.max(currentParts.length, minParts.length);

  // Compare each part of the version
  for (let i = 0; i < len; i++) {
    const current = currentParts[i] || 0; // Default to 0 if part doesn't exist
    const min = minParts[i] || 0;

    if (current < min) {
      return true; // Update required
    }
    if (current > min) {
      return false; // No update needed
    }
    // If equal, continue to next part
  }

  // If all parts are equal, no update is needed
  return false;
};

//--------------------------------------------------

/**
 * Detects if the user is on iOS or Mac (works in both native and web)
 * For web: detects iOS devices and Mac (Mac users likely have iPhone)
 * @returns true if iOS device or Mac is detected
 */
const isIOSDevice = (): boolean => {
  // For native apps, use Capacitor platform detection
  if (Capacitor.isNativePlatform()) {
    return Capacitor.getPlatform() === "ios";
  }

  // For web, detect iOS devices and Mac using user agent
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
  const isMac = /Macintosh|Mac OS X/.test(userAgent);
  
  // Return true for iOS devices or Mac (Mac users likely want App Store)
  return isIOS || isMac;
};

//--------------------------------------------------

/**
 * Redirects the user to the appropriate app store for updates
 * Uses Capacitor Browser plugin for native platforms
 * Falls back to window.open for web platform
 */
export const redirectToStore = async () => {
  // TODO: Replace with your actual App Store URL
  // const appStoreUrl = "https://apps.apple.com/search?q=RotoGPT"; 
  // const playStoreUrl = "https://play.google.com/store/search?q=RotoGPT&c=apps";

  // REAL ONES:
  const appStoreUrl = "https://apps.apple.com/us/app/rotogpt/id6736617092"; 
  const playStoreUrl = "https://play.google.com/store/apps/details?id=com.rotogpt.chat";

  //ELIMINAR ESTE BLOQUE CONDICONAL CUNADO QUIERA REDIRECCIONAR EN SERIO A LA APPSTORE/PLAYSTORE (Y SETEAR LAS URLS ARRIBA)
  // For testing purposes, we can log instead of redirecting if the URL is a placeholder
  // if (appStoreUrl.includes("YOUR_APP_ID") || playStoreUrl.includes("id=com.rotogpt.chat")) {
  //   console.log("[Update Check] Upgrade button clicked! Would redirect to the store.");
  //   return; // Stop execution here for the test
  // }

  // Determine which URL to use based on platform (works in both native and web)
  // const url = isPlatform("ios") ? appStoreUrl : playStoreUrl;
  const url = isIOSDevice() ? appStoreUrl : playStoreUrl;

  try {
    if (isPlatform("hybrid")) {
      // Use Capacitor Browser for native platforms
      await Browser.open({ url });
    } else {
      // Fallback for web platform
      window.open(url, "_blank");
    }
  } catch (error) {
    console.error("Error opening store:", error);

    // Additional fallback: try to open in new tab
    if (isPlatform("hybrid")) {
      try {
        window.open(url, "_blank");
      } catch (fallbackError) {
        console.error("Fallback also failed:", fallbackError);
      }
    }
  }
};
